<template>
  <div class="clients-phone-tab">
    <!-- Header -->
    <header class="page-header">
      <div class="header-content">
        <div>
          <h1 class="page-title">📱 Номера клиентов</h1>
          <p class="page-subtitle">Управление базой клиентов и номеров телефонов</p>
        </div>
        <div class="header-actions">
          <button @click="syncAllClients" class="btn-secondary" :disabled="syncing">
            <span class="btn-icon">🔄</span>
            {{ syncing ? 'Синхронизация...' : 'Синхронизировать базу' }}
          </button>
          <button @click="fetchData" class="btn-primary">
            <span class="btn-icon">↻</span>
            Обновить
          </button>
        </div>
      </div>
    </header>

    <!-- Advanced Stats -->
    <div class="stats-grid">
      <div 
        class="stat-card total clickable" 
        :class="{ active: filterStatus === 'all' }"
        @click="setFilter('all')"
      >
        <div class="stat-header">
          <span class="stat-icon">👥</span>
        </div>
        <div class="stat-value">{{ stats.totalClients || 0 }}</div>
        <div class="stat-label">Всего клиентов</div>
        <div class="stat-footer">В базе данных</div>
      </div>

      <div 
        class="stat-card success clickable" 
        :class="{ active: filterStatus === 'with_phone' }"
        @click="setFilter('with_phone')"
      >
        <div class="stat-header">
          <span class="stat-icon">✅</span>
        </div>
        <div class="stat-value">{{ stats.withPhone || 0 }}</div>
        <div class="stat-label">С номерами</div>
        <div class="stat-footer">{{ phonePercentage }}% от общего</div>
      </div>

      <div 
        class="stat-card warning clickable" 
        :class="{ active: filterStatus === 'without_phone' }"
        @click="setFilter('without_phone')"
      >
        <div class="stat-header">
          <span class="stat-icon">⚠️</span>
        </div>
        <div class="stat-value">{{ stats.withoutPhone || 0 }}</div>
        <div class="stat-label">Без номеров</div>
        <div class="stat-footer">{{ noPhonePercentage }}% от общего</div>
      </div>

      <div 
        class="stat-card urgent clickable" 
        :class="{ active: filterStatus === 'urgent' }"
        @click="setFilter('urgent')"
      >
        <div class="stat-header">
          <span class="stat-icon">🚨</span>
        </div>
        <div class="stat-value">{{ stats.phonesNeeded || 0 }}</div>
        <div class="stat-label">Срочно нужны</div>
        <div class="stat-footer">Активные заказы</div>
      </div>

      <div class="stat-card info">
        <div class="stat-header">
          <span class="stat-icon">📊</span>
        </div>
        <div class="stat-value">{{ stats.processed || 0 }}</div>
        <div class="stat-label">Отправлено</div>
        <div class="stat-footer">Всего уведомлений</div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <span class="stat-icon">📅</span>
        </div>
        <div class="stat-value">{{ stats.processedToday || 0 }}</div>
        <div class="stat-label">Сегодня</div>
        <div class="stat-footer">За последние 24ч</div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="search-box-large">
        <span class="search-icon">🔍</span>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Поиск по имени, ID или номеру..."
          class="search-input"
        />
      </div>
      
      <select v-model="filterStatus" class="filter-select">
        <option value="all">Все клиенты ({{ stats.totalClients }})</option>
        <option value="with_phone">С номерами ({{ stats.withPhone }})</option>
        <option value="without_phone">Без номеров ({{ stats.withoutPhone }})</option>
        <option value="urgent">Срочно нужны ({{ stats.phonesNeeded }})</option>
      </select>

      <select v-model="sortBy" class="filter-select">
        <option value="updated_desc">📅 Обновлены недавно</option>
        <option value="updated_asc">📅 Давно не обновлялись</option>
        <option value="id_desc">🔢 ID убывание</option>
        <option value="id_asc">🔢 ID возрастание</option>
        <option value="name_asc">🔤 По имени А-Я</option>
      </select>

      <button @click="resetFilters" class="btn-icon-only" title="Сбросить фильтры">
        <span>↺</span>
      </button>
    </div>

    <!-- Clients Table -->
    <div v-if="filteredClients.length > 0" class="table-container">
      <div class="table-info">
        <span>Показано: {{ filteredClients.length }} из {{ clients.length }}</span>
      </div>
      
      <table class="clients-table">
        <thead>
          <tr>
            <th style="width: 80px">ID</th>
            <th>ФИО</th>
            <th style="width: 180px">Номер телефона</th>
            <th style="width: 120px">Статус</th>
            <th style="width: 160px">Обновлено</th>
            <th style="width: 240px">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="client in filteredClients" :key="client.IDClient">
            <td>
              <span class="client-id">#{{ client.IDClient }}</span>
            </td>
            <td>
              <span class="client-name">{{ client.FIO || 'Не указано' }}</span>
            </td>
            <td>
              <div v-if="hasPermission('view_phone_numbers')">
                <div v-if="editingClient === client.IDClient" class="phone-input-container">
                  <span class="phone-prefix">+</span>
                  <input
                    v-model="editingPhone"
                    type="tel"
                    class="phone-input"
                    placeholder="77751234567"
                    maxlength="11"
                    @input="validatePhoneInput"
                    @keyup.enter="savePhone(client.IDClient)"
                    @keyup.esc="cancelEdit"
                  />
                </div>
                <span v-else class="phone-display">
                  {{ client.Phone ? '+' + client.Phone : '—' }}
                </span>
              </div>
              <span v-else class="phone-display">***</span>
            </td>
            <td>
              <span 
                v-if="client.PhoneNeeded === 1" 
                class="status-badge urgent"
              >
                🚨 Срочно
              </span>
              <span 
                v-else-if="client.Phone" 
                class="status-badge success"
              >
                ✓ Есть номер
              </span>
              <span 
                v-else 
                class="status-badge warning"
              >
                ⚠ Нет номера
              </span>
            </td>
            <td>
              <span class="timestamp">{{ formatDate(client.UpdatedAt) }}</span>
            </td>
            <td>
              <div class="action-buttons" v-if="hasPermission('view_phone_numbers')">
                <button
                  v-if="editingClient === client.IDClient"
                  @click="savePhone(client.IDClient)"
                  class="btn-save"
                  :disabled="saving || (!isPhoneValid && editingPhone.length > 0)"
                >
                  💾 Сохранить
                </button>
                <button
                  v-if="editingClient === client.IDClient && client.Phone"
                  @click="deletePhone(client.IDClient)"
                  class="btn-delete"
                  :disabled="saving"
                  title="Удалить номер"
                >
                  🗑️ Удалить
                </button>
                <button
                  v-if="editingClient === client.IDClient"
                  @click="cancelEdit"
                  class="btn-cancel"
                >
                  ✖ Отмена
                </button>
                <button
                  v-else
                  @click="startEdit(client)"
                  class="btn-edit"
                >
                  ✏️ {{ client.Phone ? 'Изменить' : 'Добавить' }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">{{ getEmptyIcon() }}</div>
      <h3 class="empty-title">{{ getEmptyTitle() }}</h3>
      <p class="empty-text">{{ getEmptyText() }}</p>
    </div>

    <!-- Notification -->
    <transition name="notification">
      <div v-if="notification.show" class="notification" :class="notification.type">
        <span>{{ notification.message }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { usePermissions } from '@/composables/usePermissions'

export default {
  name: 'ClientsPhoneTab',
  props: {
    clients: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Object,
      default: () => ({
        totalClients: 0,
        withPhone: 0,
        withoutPhone: 0,
        phonesNeeded: 0,
        processed: 0,
        processedToday: 0
      })
    }
  },
  emits: ['sync', 'refresh', 'update-phone', 'delete-phone'],
  setup(props, { emit }) {
    const { hasPermission } = usePermissions()
    const saving = ref(false)
    const syncing = ref(false)

    const searchQuery = ref('')
    const filterStatus = ref('all')
    const sortBy = ref('updated_desc')
    const editingClient = ref(null)
    const editingPhone = ref('')
    const isPhoneValid = ref(true)

    const notification = ref({
      show: false,
      message: '',
      type: 'success'
    })

    // Computed
    const phonePercentage = computed(() => {
      if (!props.stats.totalClients) return 0
      return Math.round((props.stats.withPhone / props.stats.totalClients) * 100)
    })

    const noPhonePercentage = computed(() => {
      if (!props.stats.totalClients) return 0
      return Math.round((props.stats.withoutPhone / props.stats.totalClients) * 100)
    })

    const filteredClients = computed(() => {
      let filtered = [...props.clients]

      // Фильтр по статусу
      if (filterStatus.value === 'with_phone') {
        filtered = filtered.filter(c => c.Phone)
      } else if (filterStatus.value === 'without_phone') {
        filtered = filtered.filter(c => !c.Phone)
      } else if (filterStatus.value === 'urgent') {
        filtered = filtered.filter(c => c.PhoneNeeded === 1)
      }

      // Поиск
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(client => {
          const fio = (client.FIO || '').toLowerCase()
          const id = String(client.IDClient)
          const phone = (client.Phone || '').toLowerCase()
          return fio.includes(query) || id.includes(query) || phone.includes(query)
        })
      }

      // Сортировка
      filtered.sort((a, b) => {
        switch (sortBy.value) {
          case 'updated_desc':
            return new Date(b.UpdatedAt) - new Date(a.UpdatedAt)
          case 'updated_asc':
            return new Date(a.UpdatedAt) - new Date(b.UpdatedAt)
          case 'id_desc':
            return b.IDClient - a.IDClient
          case 'id_asc':
            return a.IDClient - b.IDClient
          case 'name_asc':
            return (a.FIO || '').localeCompare(b.FIO || '')
          default:
            return 0
        }
      })

      return filtered
    })

    // Methods
    const setFilter = (filter) => {
      filterStatus.value = filter
    }

    const validatePhoneInput = () => {
      // Разрешаем только цифры
      editingPhone.value = editingPhone.value.replace(/\D/g, '')
      // Проверяем длину - валидно если пустое (для удаления) или 11 цифр
      isPhoneValid.value = editingPhone.value.length === 0 || editingPhone.value.length === 11
    }

    const syncAllClients = async () => {
      if (!confirm('Синхронизировать все данные из Access? Это может занять некоторое время.')) {
        return
      }

      syncing.value = true
      try {
        await emit('sync')
        showNotification('Синхронизация запущена. Данные будут обновлены.', 'success')
      } catch (error) {
        console.error('Error syncing clients:', error)
        showNotification('Ошибка при синхронизации', 'error')
      } finally {
        syncing.value = false
      }
    }

    const fetchData = () => {
      emit('refresh')
    }

    const startEdit = (client) => {
      editingClient.value = client.IDClient
      editingPhone.value = client.Phone || ''
      isPhoneValid.value = true
    }

    const cancelEdit = () => {
      editingClient.value = null
      editingPhone.value = ''
      isPhoneValid.value = true
    }

    const savePhone = async (clientId) => {
      // Если поле пустое, это считается удалением
      if (editingPhone.value.length === 0) {
        await deletePhone(clientId)
        return
      }

      if (!isPhoneValid.value) {
        showNotification('Номер должен содержать 11 цифр', 'error')
        return
      }

      saving.value = true
      try {
        await emit('update-phone', { clientId, phone: editingPhone.value })
        showNotification('Номер успешно сохранен', 'success')
        cancelEdit()
      } catch (error) {
        console.error('Error saving phone:', error)
        showNotification('Ошибка при сохранении номера', 'error')
      } finally {
        saving.value = false
      }
    }

    const deletePhone = async (clientId) => {
      if (!confirm('Вы уверены, что хотите удалить номер телефона?')) {
        return
      }

      saving.value = true
      try {
        await emit('delete-phone', clientId)
        showNotification('Номер успешно удален', 'success')
        cancelEdit()
      } catch (error) {
        console.error('Error deleting phone:', error)
        showNotification('Ошибка при удалении номера', 'error')
      } finally {
        saving.value = false
      }
    }

    const resetFilters = () => {
        searchQuery.value = ''
        filterStatus.value = 'all'
        sortBy.value = 'updated_desc'
        showNotification('Фильтры сброшены', 'success')
      }


    const showNotification = (message, type = 'success') => {
      notification.value = { show: true, message, type }
      setTimeout(() => {
        notification.value.show = false
      }, 3000)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '—'
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'только что'
      if (diffMins < 60) return `${diffMins} мин назад`
      if (diffHours < 24) return `${diffHours} ч назад`
      if (diffDays < 7) return `${diffDays} дн назад`
      
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const getEmptyIcon = () => {
      if (searchQuery.value) return '🔍'
      if (filterStatus.value === 'urgent') return '🚨'
      if (filterStatus.value === 'with_phone') return '✅'
      if (filterStatus.value === 'without_phone') return '⚠️'
      return '👥'
    }

    const getEmptyTitle = () => {
      if (searchQuery.value) return 'Ничего не найдено'
      if (filterStatus.value === 'urgent') return 'Нет срочных клиентов'
      if (filterStatus.value === 'with_phone') return 'Нет клиентов с номерами'
      if (filterStatus.value === 'without_phone') return 'Все клиенты имеют номера!'
      return 'Нет клиентов'
    }

    const getEmptyText = () => {
      if (searchQuery.value) return 'Попробуйте изменить поисковый запрос'
      if (filterStatus.value === 'urgent') return 'Отлично! В данный момент нет клиентов, которым срочно нужны номера'
      if (filterStatus.value === 'with_phone') return 'Начните добавлять номера клиентам'
      if (filterStatus.value === 'without_phone') return 'У всех клиентов есть номера телефонов'
      return 'База клиентов пуста'
    }

    return {
      saving,
      syncing,
      searchQuery,
      filterStatus,
      sortBy,
      editingClient,
      editingPhone,
      isPhoneValid,
      notification,
      phonePercentage,
      noPhonePercentage,
      filteredClients,
      setFilter,
      validatePhoneInput,
      fetchData,
      startEdit,
      cancelEdit,
      savePhone,
      deletePhone,
      syncAllClients,
      resetFilters,
      formatDate,
      getEmptyIcon,
      getEmptyTitle,
      getEmptyText,
      hasPermission
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.clients-phone-tab {
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* HEADER */
.page-header {
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  color: #6b7280;
  font-size: 15px;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
}

.btn-secondary {
  background: #fff;
  color: #6366f1;
  border: 2px solid #e5e7eb;
}

.btn-secondary:hover:not(:disabled) {
  border-color: #6366f1;
  transform: translateY(-2px);
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 18px;
}

/* STATS GRID */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  padding: 24px;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.stat-card.clickable {
  cursor: pointer;
}

.stat-card.clickable:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #6366f1;
}

.stat-card.active {
  border-color: #6366f1;
  background: linear-gradient(135deg, #f0f1ff 0%, #ffffff 100%);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.2);
}

.stat-card.active::before {
  content: '✓';
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  background: #6366f1;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
}

.stat-card.total { border-left: 4px solid #6366f1; }
.stat-card.success { border-left: 4px solid #10b981; }
.stat-card.warning { border-left: 4px solid #f59e0b; }
.stat-card.urgent { border-left: 4px solid #ef4444; }
.stat-card.info { border-left: 4px solid #3b82f6; }

.stat-header {
  margin-bottom: 12px;
}

.stat-icon {
  font-size: 32px;
}

.stat-value {
  font-size: 36px;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: -1px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-footer {
  font-size: 13px;
  color: #9ca3af;
}

/* FILTERS */
.filters-section {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
}

.search-box-large {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.filter-select {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 200px;
}

.filter-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.btn-icon-only {
  width: 46px;
  height: 46px;
  border: 2px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon-only:hover {
  border-color: #6366f1;
  color: #6366f1;
  transform: rotate(180deg);
}

/* LOADING */
.loading-container {
  text-align: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* TABLE */
.table-container {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.table-info {
  padding: 15px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.clients-table {
  width: 100%;
  border-collapse: collapse;
}

.clients-table thead {
  background: #f9fafb;
}

.clients-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e5e7eb;
}

.clients-table tbody tr {
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s;
}

.clients-table tbody tr:hover {
  background: #f9fafb;
}

.clients-table tbody tr:last-child {
  border-bottom: none;
}

.clients-table td {
  padding: 18px 20px;
  vertical-align: middle;
}

.client-id {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: #6366f1;
  font-size: 14px;
}

.client-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 15px;
}

.phone-input-container {
  display: flex;
  align-items: center;
  gap: 4px;
}

.phone-prefix {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #6b7280;
  font-size: 14px;
}

.phone-display {
  font-family: 'Courier New', monospace;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
}

.phone-input {
  width: 140px;
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  transition: all 0.2s;
}

.phone-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.status-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}

.status-badge.success {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.warning {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.urgent {
  background: #fee2e2;
  color: #991b1b;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.timestamp {
  font-size: 13px;
  color: #9ca3af;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-save,
.btn-edit,
.btn-cancel,
.btn-delete {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-save {
  background: #10b981;
  color: #fff;
}

.btn-save:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-edit {
  background: #6366f1;
  color: #fff;
}

.btn-edit:hover {
  background: #4f46e5;
  transform: translateY(-1px);
}

.btn-delete {
  background: #ef4444;
  color: #fff;
}

.btn-delete:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-cancel {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 100px 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.empty-text {
  color: #6b7280;
  font-size: 15px;
}

/* NOTIFICATION */
.notification {
  position: fixed;
  bottom: 30px;
  right: 30px;
  padding: 16px 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  font-weight: 600;
  z-index: 1000;
  max-width: 400px;
}

.notification.success {
  border-left: 4px solid #10b981;
  color: #065f46;
}

.notification.error {
  border-left: 4px solid #ef4444;
  color: #991b1b;
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from,
.notification-leave-to {
  transform: translateX(400px);
  opacity: 0;
}

/* RESPONSIVE */
@media (max-width: 1200px) {
  .filters-section {
    grid-template-columns: 1fr;
  }
  
  .filter-select {
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .clients-table {
    min-width: 900px;
  }
  
  .notification {
    left: 20px;
    right: 20px;
  }
}
</style>